import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { KeyRound, Search, Lock, Link2, Eye, X } from 'lucide-react';
import api from '../../utils/api';

// One-click "Compartir acceso": pick a company-scoped vault entry → generate a one-time
// secure link (or inline plaintext) → hand the composed message back to the caller, which
// drops it into the WhatsApp composer / ticket reply. Gated by the vault PIN; AUDITED.
export default function ShareCredentialModal({ companyId, companyName, contactId, defaultQuery, onInsert, onClose }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [allEntries, setAllEntries] = useState(null); // company list (null until loaded)
  const [results, setResults] = useState([]);          // fallback search (no companyId)
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('link');       // 'link' | 'plain'
  const [expiry, setExpiry] = useState(60);        // minutes
  const [generating, setGenerating] = useState(false);
  const [pinNeeded, setPinNeeded] = useState(false);
  const [pin, setPin] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);
  const debounce = useRef(null);

  // Strictly scope the picker to the contact's company — load that company's entries up
  // front so the agent can't pick another client's credential. No company → fall back to
  // a name search across what the agent can access.
  const loadEntries = useCallback(async () => {
    if (!companyId) { setAllEntries(null); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/vault/company/${companyId}/entries`);
      setAllEntries(data.entries || []);
      setPinNeeded(false);
    } catch (err) {
      if (err.response?.data?.code === 'VAULT_REQUIRED' || err.response?.data?.code === 'VAULT_EXPIRED') setPinNeeded(true);
    } finally { setSearching(false); }
  }, [companyId]);

  const doSearch = useCallback(async (q) => {
    if (companyId) return; // company list path handles it client-side
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get('/vault/search', { params: { q: q.trim() } });
      setResults((data.entries || []).slice(0, 25));
      setPinNeeded(false);
    } catch (err) {
      if (err.response?.data?.code === 'VAULT_REQUIRED' || err.response?.data?.code === 'VAULT_EXPIRED') setPinNeeded(true);
    } finally { setSearching(false); }
  }, [companyId]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  useEffect(() => {
    if (companyId) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(debounce.current);
  }, [query, doSearch, companyId]);

  // What the list shows: company entries filtered client-side, or the fallback search.
  const ql = query.trim().toLowerCase();
  const visibleEntries = companyId
    ? (allEntries || []).filter(e => !ql
        || (e.entry_name || '').toLowerCase().includes(ql)
        || (e.username || '').toLowerCase().includes(ql)
        || (e.url || '').toLowerCase().includes(ql))
    : results;

  const unlockVault = async () => {
    if (!pin || pin.length < 4) return;
    setPinSubmitting(true);
    try {
      const { data } = await api.post('/vault/pin/verify', { pin });
      if (data.vaultToken) {
        sessionStorage.setItem('vaultToken', data.vaultToken);
        setPin(''); setPinNeeded(false);
        if (companyId) loadEntries(); else doSearch(query);
      }
    } catch {
      toast.error(t('vault.invalidPin') || 'PIN incorrecto');
    } finally { setPinSubmitting(false); }
  };

  const generate = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const { data } = await api.post('/vault/share', {
        entryId: selected.id,
        title: selected.entry_name,
        contactId: contactId || undefined,
        mode,
        expiresMinutes: mode === 'link' ? expiry : undefined,
      });
      onInsert?.(data.message);
      toast.success(mode === 'link'
        ? (t('vault.shareModal.linkInserted') || 'Enlace seguro insertado en el mensaje')
        : (t('vault.shareModal.credInserted') || 'Credencial insertada en el mensaje'));
      onClose?.();
    } catch (err) {
      if (err.response?.data?.code === 'VAULT_REQUIRED' || err.response?.data?.code === 'VAULT_EXPIRED') {
        setPinNeeded(true);
      } else {
        toast.error(err.response?.data?.error || t('common.error'));
      }
    } finally { setGenerating(false); }
  };

  const modal = (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) { (onClose)(e); } }} style={{
      position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'rgba(0,0,0,0.5)', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ margin: 0, maxWidth: 460, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyRound size={18} /> {t('vault.shareModal.title') || 'Compartir acceso'}
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {pinNeeded ? (
          <div style={{ padding: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: 'var(--c-text-muted)', fontSize: 13 }}>
              <Lock size={16} /> {t('vault.shareModal.unlockPrompt') || 'Desbloqueá la bóveda con tu PIN para compartir un acceso.'}
            </div>
            <div className="form-group">
              <input type="password" className="form-input" value={pin} autoFocus
                onChange={e => setPin(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && unlockVault()}
                placeholder={t('vault.enterPin') || 'PIN de la bóveda'} />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} disabled={pinSubmitting || pin.length < 4} onClick={unlockVault}>
              {pinSubmitting ? '…' : (t('vault.unlock') || 'Desbloquear')}
            </button>
          </div>
        ) : (
          <div style={{ padding: 8 }}>
            {/* Company scope chip */}
            {companyId && companyName && (
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Lock size={11} /> {(t('vault.shareModal.scopedTo') || 'Solo credenciales de')}: <strong>{companyName}</strong>
              </div>
            )}
            {/* Search */}
            <div className="form-group" style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--c-text-muted)' }} />
              <input className="form-input" style={{ paddingLeft: 30 }} value={query} autoFocus
                onChange={e => { setQuery(e.target.value); setSelected(null); }}
                placeholder={t('vault.shareModal.searchPlaceholder') || 'Buscar credencial…'} />
            </div>

            {/* Results */}
            {!selected && (
              <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 8 }}>
                {searching && <div style={{ padding: 12, color: 'var(--c-text-muted)', fontSize: 13 }}>{t('common.loading') || 'Cargando…'}</div>}
                {!searching && visibleEntries.length === 0 && (companyId || query.trim().length >= 2) && (
                  <div style={{ padding: 12, color: 'var(--c-text-muted)', fontSize: 13 }}>{t('vault.shareModal.noResults') || 'Sin resultados.'}</div>
                )}
                {visibleEntries.map(e => (
                  <div key={e.id} onClick={() => setSelected(e)} style={{
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                    border: '1px solid var(--c-border)', background: 'var(--c-surface-2)',
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{e.entry_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--c-text-muted)', display: 'flex', gap: 8 }}>
                      {e.company_name && <span>{e.company_name}</span>}
                      {e.username && <span>· {e.username}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected + options */}
            {selected && (
              <>
                <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--c-surface-2)', border: '1px solid var(--c-brand)', marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{selected.entry_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                    {selected.company_name}{selected.username ? ` · ${selected.username}` : ''}
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, padding: '2px 6px' }} onClick={() => setSelected(null)}>
                    {t('common.change') || 'Cambiar'}
                  </button>
                </div>

                {/* Mode toggle */}
                <div className="form-group">
                  <label className="form-label">{t('vault.shareModal.howToSend') || '¿Cómo enviarlo?'}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className={`btn btn-sm ${mode === 'link' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }} onClick={() => setMode('link')}>
                      <Link2 size={13} /> {t('vault.shareModal.secureLink') || 'Enlace seguro'}
                    </button>
                    <button className={`btn btn-sm ${mode === 'plain' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }} onClick={() => setMode('plain')}>
                      <Eye size={13} /> {t('vault.shareModal.inline') || 'Texto directo'}
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 6 }}>
                    {mode === 'link'
                      ? (t('vault.shareModal.secureLinkHint') || 'Un link de un solo uso que caduca. La clave no queda en el chat. (Recomendado)')
                      : (t('vault.shareModal.inlineHint') || '⚠ La clave va en texto plano en el mensaje y queda en el historial de WhatsApp.')}
                  </div>
                </div>

                {mode === 'link' && (
                  <div className="form-group">
                    <label className="form-label">{t('vault.shareModal.expiresIn') || 'Caduca en'}</label>
                    <select className="form-select" value={expiry} onChange={e => setExpiry(parseInt(e.target.value))}>
                      <option value={15}>15 min</option>
                      <option value={60}>1 h</option>
                      <option value={240}>4 h</option>
                      <option value={1440}>24 h</option>
                    </select>
                  </div>
                )}

                <button className="btn btn-primary" style={{ width: '100%' }} disabled={generating} onClick={generate}>
                  {generating ? '…' : (t('vault.shareModal.generateInsert') || 'Generar e insertar en el mensaje')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
